import React, { useState } from "react";
import * as Icons from "lucide-react";
import SectionTitle from "./SectionTitle";
import ProjectImagesLightbox from "./ProjectImagesLightbox";
import { useHome } from "../api/queries";

const { Images } = Icons;

export default function Sectors() {
  const { data, isLoading } = useHome();
  const sectors = data?.sectors ?? [];
  const [activeSector, setActiveSector] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  if (!isLoading && sectors.length === 0) return null;

  const openSectorGallery = (sector) => {
    if (!sector.imageUrls?.length) return;
    setActiveSector(sector);
    setLightboxIndex(0);
  };

  const closeLightbox = () => {
    setActiveSector(null);
    setLightboxIndex(0);
  };

  return (
    <section className="relative py-24 bg-[#0a1a2f] overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <SectionTitle
          eyebrow="Sectors We Serve"
          title="Strengthening Infrastructure Across Industries"
          subtitle="Our specialised engineering interventions touch every facet of India's built environment — from power and transport to defence and commercial."
          light
        />

        {isLoading ? (
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div key={n} className="p-6 rounded-xl border border-white/10 bg-white/[0.03] animate-pulse">
                <div className="w-14 h-14 rounded-lg bg-white/10" />
                <div className="mt-5 h-4 bg-white/10 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {sectors.map((s) => {
              const Icon = Icons[s.icon] || Icons.Building2;
              const hasImages = s.imageUrls?.length > 0;
              const CardTag = hasImages ? "button" : "div";

              return (
                <CardTag
                  key={s.id}
                  type={hasImages ? "button" : undefined}
                  onClick={hasImages ? () => openSectorGallery(s) : undefined}
                  className={`group relative p-6 rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-sm transition-all duration-300 text-left w-full ${
                    hasImages
                      ? "hover:border-[#1e6fcf]/50 hover:bg-white/[0.07] cursor-pointer"
                      : "hover:border-[#1e6fcf]/50 hover:bg-white/[0.07]"
                  }`}
                >
                  <div className="w-14 h-14 rounded-lg bg-[#1e6fcf]/10 group-hover:bg-[#1e6fcf] flex items-center justify-center transition-all duration-300 overflow-hidden">
                    {s.iconUrl ? (
                      <img src={s.iconUrl} alt="" className="w-8 h-8 object-contain" loading="lazy" />
                    ) : (
                      <Icon className="w-7 h-7 text-[#1e6fcf] group-hover:text-white transition-colors" />
                    )}
                  </div>
                  <div className="mt-5 text-white font-bold text-base leading-tight">{s.name}</div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="w-8 h-0.5 bg-[#1e6fcf]/40 group-hover:w-16 transition-all duration-500" />
                    {hasImages && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#aad4fa]/80 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Images className="w-3.5 h-3.5" />
                        {s.imageUrls.length}
                      </span>
                    )}
                  </div>
                </CardTag>
              );
            })}
          </div>
        )}
      </div>

      {activeSector?.imageUrls?.length > 0 && (
        <ProjectImagesLightbox
          images={activeSector.imageUrls}
          activeIndex={lightboxIndex}
          title={activeSector.name}
          size="compact"
          onClose={closeLightbox}
          onNavigate={setLightboxIndex}
        />
      )}
    </section>
  );
}
